import { format } from 'date-fns';
import { hi, enIN } from 'date-fns/locale';

// src/format/formatGrams.ts
var DEFAULT_LOCALE = "en-IN";
var DEFAULT_DECIMALS = 4;
function formatGrams(value, options = {}) {
  const { decimals = DEFAULT_DECIMALS, locale = DEFAULT_LOCALE } = options;
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
    // A gram weight arrives as a wire decimal (see formatINR's toDisplayNumber
    // note): the string→number conversion belongs here, at the render boundary,
    // and nowhere upstream of it.
  }).format(typeof value === "number" ? value : Number(value));
  return `${formatted} g`;
}

// src/format/formatINR.ts
var DEFAULT_LOCALE2 = "en-IN";
function toDisplayNumber(value) {
  if (typeof value === "number") return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}
function formatINR(value, options = {}) {
  const { showSymbol = true, locale = DEFAULT_LOCALE2 } = options;
  const numberFormatOptions = showSymbol ? { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 } : { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return new Intl.NumberFormat(locale, numberFormatOptions).format(toDisplayNumber(value));
}

// src/format/formatPercent.ts
var DEFAULT_DECIMALS2 = 2;
function formatPercent(value, decimals = DEFAULT_DECIMALS2) {
  const rounded = Number(value.toFixed(decimals));
  const normalized = rounded === 0 ? 0 : rounded;
  return `${normalized.toFixed(decimals)}%`;
}

// src/format/formatCompactNumber.ts
var INDIC_LOCALES = /* @__PURE__ */ new Set(["en-IN", "hi-IN", "en", "hi"]);
var CRORE = 1e7;
var LAKH = 1e5;
var WESTERN_BILLION = 1e9;
var WESTERN_MILLION = 1e6;
var THOUSAND = 1e3;
function formatCompactNumber(value, options = {}) {
  const { locale = "en-IN" } = options;
  const sign = value < 0 ? "-" : "";
  const magnitude = Math.abs(value);
  if (INDIC_LOCALES.has(locale)) {
    if (magnitude >= CRORE) return `${sign}${trimTrailingZero((magnitude / CRORE).toFixed(1))}Cr`;
    if (magnitude >= LAKH) return `${sign}${trimTrailingZero((magnitude / LAKH).toFixed(1))}L`;
    if (magnitude >= THOUSAND) return `${sign}${trimTrailingZero((magnitude / THOUSAND).toFixed(1))}K`;
    return `${sign}${magnitude}`;
  }
  if (magnitude >= WESTERN_BILLION) return `${sign}${trimTrailingZero((magnitude / WESTERN_BILLION).toFixed(1))}B`;
  if (magnitude >= WESTERN_MILLION) return `${sign}${trimTrailingZero((magnitude / WESTERN_MILLION).toFixed(1))}M`;
  if (magnitude >= THOUSAND) return `${sign}${trimTrailingZero((magnitude / THOUSAND).toFixed(1))}K`;
  return `${sign}${magnitude}`;
}
function trimTrailingZero(formatted) {
  return formatted.endsWith(".0") ? formatted.slice(0, -2) : formatted;
}

// src/validation/isValidPhone.ts
var INDIAN_MOBILE_REGEX = /^(?:\+91)?[6-9]\d{9}$/;
function isValidPhone(value) {
  if (typeof value !== "string") return false;
  return INDIAN_MOBILE_REGEX.test(value.trim());
}

// src/validation/isValidPan.ts
var PAN_REGEX = /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/;
function isValidPan(value) {
  if (typeof value !== "string") return false;
  return PAN_REGEX.test(value.trim());
}

// src/validation/isValidAadhaarMasked.ts
var MASKED_AADHAAR_REGEX = /^XXXX XXXX \d{4}$/;
function isValidAadhaarMasked(value) {
  if (typeof value !== "string") return false;
  return MASKED_AADHAAR_REGEX.test(value.trim());
}

// src/validation/isValidOtp.ts
var DEFAULT_OTP_LENGTH = 6;
function isValidOtp(value, length = DEFAULT_OTP_LENGTH) {
  if (typeof value !== "string") return false;
  if (!Number.isInteger(length) || length <= 0) return false;
  const pattern = new RegExp(`^\\d{${length}}$`);
  return pattern.test(value.trim());
}

// src/date/formatRelativeDate.ts
var DEFAULT_LOCALE3 = "en-IN";
var DIVISIONS = [
  { amount: 60, unit: "seconds" },
  { amount: 60, unit: "minutes" },
  { amount: 24, unit: "hours" },
  { amount: 7, unit: "days" },
  { amount: 4.34524, unit: "weeks" },
  { amount: 12, unit: "months" },
  { amount: Number.POSITIVE_INFINITY, unit: "years" }
];
function formatRelativeDate(date) {
  const target = typeof date === "string" ? new Date(date) : date;
  const formatter = new Intl.RelativeTimeFormat(DEFAULT_LOCALE3, { numeric: "auto" });
  let duration = (target.getTime() - Date.now()) / 1e3;
  if (Number.isNaN(duration)) return "Invalid date";
  for (const division of DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return formatter.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return formatter.format(Math.round(duration), "years");
}
var DEFAULT_PATTERN = "dd MMM yyyy";
var DEFAULT_LOCALE4 = "en-IN";
var LOCALE_MAP = {
  "en-IN": enIN,
  en: enIN,
  "hi-IN": hi,
  hi
};
function formatDate(date, pattern = DEFAULT_PATTERN, locale = DEFAULT_LOCALE4) {
  const target = typeof date === "string" ? new Date(date) : date;
  const resolvedLocale = LOCALE_MAP[locale] ?? enIN;
  return format(target, pattern, { locale: resolvedLocale });
}

// src/date/holidays.ts
var BANK_HOLIDAYS = /* @__PURE__ */ new Set([
  // 2026
  "2026-01-26",
  // Republic Day
  "2026-03-04",
  // Holi
  "2026-04-03",
  // Good Friday
  "2026-08-15",
  // Independence Day
  "2026-10-02",
  // Gandhi Jayanti
  "2026-10-20",
  // Dussehra
  "2026-11-08",
  // Diwali
  "2026-12-25"
  // Christmas
]);
function isBankHoliday(date) {
  return BANK_HOLIDAYS.has(toIsoDate(date));
}
function toIsoDate(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// src/date/isBusinessDay.ts
var SUNDAY = 0;
var SATURDAY = 6;
function isBusinessDay(date) {
  const day = date.getDay();
  if (day === SUNDAY || day === SATURDAY) return false;
  return !isBankHoliday(date);
}

// src/date/addBusinessDays.ts
function addBusinessDays(date, days) {
  const result = new Date(date.getTime());
  const direction = days < 0 ? -1 : 1;
  let remaining = Math.abs(Math.trunc(days));
  while (remaining > 0) {
    result.setDate(result.getDate() + direction);
    if (isBusinessDay(result)) {
      remaining -= 1;
    }
  }
  return result;
}

// src/id/generateCorrelationId.ts
function generateCorrelationId() {
  const cryptoObj = getCrypto();
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    return uuidFromRandomBytes(cryptoObj);
  }
  return uuidFromMathRandom();
}
function getCrypto() {
  if (typeof globalThis !== "undefined" && "crypto" in globalThis) {
    return globalThis.crypto;
  }
  return void 0;
}
function uuidFromRandomBytes(cryptoObj) {
  const bytes = new Uint8Array(16);
  cryptoObj.getRandomValues(bytes);
  const versionByte = bytes[6];
  const variantByte = bytes[8];
  if (versionByte === void 0 || variantByte === void 0) return uuidFromMathRandom();
  bytes[6] = versionByte & 15 | 64;
  bytes[8] = variantByte & 63 | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join("")
  ].join("-");
}
function uuidFromMathRandom() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.random() * 16 | 0;
    const value = char === "x" ? random : random & 3 | 8;
    return value.toString(16);
  });
}

// src/retry/computeBackoffDelay.ts
var DEFAULT_BASE_MS = 250;
var DEFAULT_MAX_MS = 3e4;
function computeBackoffDelay(attempt, options = {}) {
  const { baseMs = DEFAULT_BASE_MS, maxMs = DEFAULT_MAX_MS } = options;
  const safeAttempt = Math.max(0, Math.trunc(attempt));
  const exponentialDelay = baseMs * 2 ** safeAttempt;
  const cappedDelay = Math.min(exponentialDelay, maxMs);
  return Math.floor(Math.random() * cappedDelay);
}

export { addBusinessDays, computeBackoffDelay, formatCompactNumber, formatDate, formatGrams, formatINR, formatPercent, formatRelativeDate, generateCorrelationId, isBusinessDay, isValidAadhaarMasked, isValidOtp, isValidPan, isValidPhone };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map